import React, { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import '../styles/Pages.css';
import images from '../assets/index';

const articles = [
    {
        id: 'article-1',
        title: 'How to Transition to a Central Semantic Layer – Accelerate Your Data Value',
        published: 'Sep 01, 2025',
        readTime: '2 min Read',
        image: images.news1,
        intro: 'Unlock the full potential of your data platform with expert-led migration to a central semantic layer, leveraging proven methodologies and accelerators.',
        sections: [
            {
                heading: 'Migration Best Practices',
                body: 'Transitioning from distributed BI semantic layers to a centralized architecture requires:\n\n1. Assessment: Inventory existing semantic models, definitions, and dependencies across BI tools.\n\n2. Design: Establish a unified, business-aligned semantic framework with flexible integration points.\n\n3. Migration: Use proven accelerators and automation tools for efficient transition, minimizing disruption.\n\n4. Change Management: Engage stakeholders, train teams, and update business processes to realize the new architecture’s benefits.'
            },
            {
                heading: 'Expert Accelerators',
                body: 'Our consultants bring:\n– Templates and automation for extracting, mapping, and migrating BI semantic layers\n– Proven frameworks for governance, integration, and performance optimization\n– Deep experience in both legacy (MicroStrategy, BusinessObjects) and modern cloud-native platforms'
            },
            {
                heading: 'The Business Case',
                body: 'Migration Accelerators → Faster delivery, lower risk, reduced cost\nDeep Expertise → Strategic guidance, business alignment, robust results\nPost-Migration Support → Continuous improvement, training, and governance'
            }
        ],
        related: ['article-2', 'article-3']
    },
    {
        id: 'article-2',
        title: 'Introduction to Semantic Layers – The Gateway to Smarter Data Analytics',
        published: 'Aug 27, 2025',
        readTime: '1 min Read',
        image: images.news2,
        intro: 'Main Takeaway: Understanding the fundamentals of semantic layers is the first step toward unlocking consistent, accurate, and business-ready data analytics across your organization.',
        sections: [
            {
                heading: 'What is a Semantic Layer?',
                body: 'A semantic layer is a translation layer between raw data sources and business users, providing a unified, business-friendly view of organizational data. It abstracts the complexities of underlying data models and technical constructs, allowing users to interact with data using familiar business terms and concepts.\n\nTraditionally, semantic layers have been a core component of BI tools such as MicroStrategy, SAP BusinessObjects, and others.'
            },
            {
                heading: 'What are the Benefits?',
                body: '– Consistent Definitions: Ensures that KPIs, metrics, and business terms mean the same thing across all reports and dashboards.\n– Self-Service Analytics: Empowers business users to query data confidently using business language.\n– Improved Data Quality: Reduces misinterpretation and errors.\n– Enhanced Collaboration: Provides a shared vocabulary for data across teams.'
            }
        ],
        related: ['article-1', 'article-3']
    },
    {
        id: 'article-3',
        title: 'Why Centralizing the Semantic Layer Trumps BI-Specific Architectures',
        published: 'Aug 27, 2025',
        readTime: '1 min Read',
        image: images.news3,
        intro: 'Main Takeaway: Moving your semantic layer out of individual BI tools and establishing it centrally within your data platform paves the way for agility, scalability, and data governance.',
        sections: [
            {
                heading: 'Distributed vs. Central Semantic Layers',
                body: 'BI Tool Semantic Layer: Logic replicated across tools; risk of inconsistency; difficult governance; vendor lock-in.\n\nCentral Data Platform Semantic Layer: One authoritative logic repository; uniform KPIs and metrics; seamless governance.'
            },
            {
                heading: 'Key Advantages of Centralizing the Semantic Layer',
                body: '– Single Source of Truth\n– Governance and Compliance\n– Accelerated Change Management\n– Simplified Migration and Integration'
            },
            {
                heading: 'Real-World Impact',
                body: 'Organizations migrating from BI-specific semantic layers report:\n– Up to 60% reduction in data reconciliation efforts\n– Improved speed and confidence in analytics rollouts\n– Enhanced agility to adapt to new business requirements'
            }
        ],
        related: ['article-1', 'article-2']
    }
];

function InsightArticle() {
    const { articleId } = useParams();
    const navigate = useNavigate();
    const article = articles.find((item) => item.id === articleId);

    const handleBack = () => {
        navigate('/insights');
        setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }, 50);
    };

    useEffect(() => {
        if (article) {
            document.title = `${article.title} | Datamatics Consulting`;
        } else {
            document.title = 'Article Not Found | Datamatics Consulting';
        }
    }, [article]);

    if (!article) {
        return <div className="page-container">Article not found.</div>;
    }

    return (
        <div className="insight-article-page">
            <section className="hero-static article-hero">
                <div className="hero-slide-wrapper">
                    <img src={images.purplap} alt={article.title} className="hero-background-image" />
                    <div className="hero-overlay">
                        <div className="hero-content article-hero-content">
                            <h1>{article.title}</h1>
                        </div>
                    </div>
                </div>
            </section>
            <section className="article-content-section container">
                <button className="article-back-btn" onClick={handleBack}>← Back</button>
                <div className="article-layout">
                    <article className="article-main">
                        <p className="article-meta">Published on: {article.published} | {article.readTime}</p>
                        <p className="article-intro">{article.intro}</p>

                        {article.sections.map((section, index) => (
                            <div key={index} className="article-section">
                                <h2>{section.heading}</h2>
                                <p style={{ whiteSpace: 'pre-line' }}>{section.body}</p>
                            </div>
                        ))}
                    </article>
                    <aside className="article-sidebar">
                        <div className="article-aside-card">
                            <h4>Articles You May Like</h4>
                            {article.related.map((relatedId) => {
                                const related = articles.find((item) => item.id === relatedId);
                                return (
                                    related && (
                                        <Link
                                            key={related.id}
                                            to={`/insights/${related.id}`}
                                            className="article-related-card"
                                        >
                                            <div
                                                className="news-card-bg"
                                                style={{ backgroundImage: `url(${related.image})` }}
                                            ></div>
                                            <div className="news-card-content">
                                                <h3>{related.title}</h3>
                                                <div className="news-card-pill-btn">
                                                    Learn More →
                                                </div>
                                            </div>
                                        </Link>
                                    )
                                );
                            })}
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

export default InsightArticle;